import { Builder } from '@builder.io/react'
import React from 'react'

import { HeadingClass, HeadingType } from 'enums'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

import './index.css'

enum Alignment {
  Center = 'center',
  Left = 'left',
  Right = 'right'
}

interface Props {
  backgroundColor: string
  enableTopic: boolean
  topicText: string
  topicTextColor: string
  headingText: string
  headingTextColor: string
  contentText: string
  textureColor: string
  headingType: HeadingType
  headingClass: HeadingClass
  textAlignment: Alignment
  belowNavbar: boolean
}

const TextNoImage = ({
  backgroundColor,
  enableTopic,
  topicText,
  topicTextColor,
  headingText,
  headingTextColor,
  contentText,
  textureColor,
  headingType,
  headingClass,
  textAlignment,
  belowNavbar
}: Props) => {
  const getTopic = () => {
    if (!enableTopic) return null
    return (
      <span
        className="block text-center text-base font-semibold uppercase tracking-wide text-indigo-600"
        style={{ color: topicTextColor }}
      >
        {topicText}
      </span>
    )
  }

  const getAlignmentClass = () => {
    if (textAlignment === Alignment.Right) {
      return 'text-right'
    }
    if (textAlignment === Alignment.Center) {
      return 'text-center'
    }
    return 'text-left'
  }

  const alignmentClass = getAlignmentClass()

  return (
    <div
      className={`relative overflow-hidden bg-white py-16 ${belowNavbar ? 'lg:-mt-12 lg:pt-12' : ''}`}
      style={{ backgroundColor }}
    >
      <div className="hidden lg:absolute lg:inset-y-0 lg:block lg:h-full lg:w-full">
        <div className="relative mx-auto h-full max-w-prose text-lg" aria-hidden="true">
          <svg
            className="absolute top-12 left-full translate-x-32 transform"
            width={404}
            height={384}
            fill="none"
            viewBox="0 0 404 384"
          >
            <defs>
              <pattern
                id="74b3fd99-0a6f-4271-bef2-e80eeafdf357"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                  style={{ color: textureColor }}
                />
              </pattern>
            </defs>
            <rect width={404} height={384} fill="url(#74b3fd99-0a6f-4271-bef2-e80eeafdf357)" />
          </svg>
          <svg
            className="absolute top-1/2 right-full -translate-y-1/2 -translate-x-32 transform"
            width={404}
            height={384}
            fill="none"
            viewBox="0 0 404 384"
          >
            <defs>
              <pattern
                id="f210dbf6-a58d-4871-961e-36d5016a0f49"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                  style={{ color: textureColor }}
                />
              </pattern>
            </defs>
            <rect width={404} height={384} fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)" />
          </svg>
        </div>
      </div>
      <div className="relative px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-prose text-lg">
          {getTopic()}
          <CustomHeading
            classList="mt-2 block text-center leading-8 font-bold tracking-tight"
            headingClass={headingClass}
            headingType={headingType}
            color={headingTextColor}
          >
            {headingText}
          </CustomHeading>
          <p
            className={`enable-list-styles mt-8 text-base ${alignmentClass}`}
            dangerouslySetInnerHTML={{ __html: contentText }}
          ></p>
        </div>
      </div>
    </div>
  )
}

export default TextNoImage

Builder.registerComponent(TextNoImage, {
  name: 'TextNoImage',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'enableTopic',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'topicText',
      type: 'text',
      defaultValue: 'INTRODUCING'
    },
    {
      name: 'topicTextColor',
      type: 'color',
      defaultValue: 'purple'
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Javascript for Beginners'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'textureColor',
      type: 'color',
      defaultValue: 'gray'
    },
    {
      name: 'contentText',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'textAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left'
    },
    {
      name: 'belowNavbar',
      type: 'boolean',
      defaultValue: false
    }
  ]
})
